import React, { useMemo, useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Space,
  Form,
  message,
  Input,
  Typography,
} from "antd";
import personaApi from "./persona.api";
import AddPersona from "./addPersona";
import {
  PlusOutlined,
  DeleteFilled,
  EditFilled,
  CloseCircleOutlined,
} from "@ant-design/icons";

const { Search } = Input;
const { Title, Paragraph } = Typography;

const PersonaTable = () => {
  const [form] = Form.useForm();
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [searchText, setSearchText] = useState("");
  const { confirm } = Modal;

  const compareText = (a, b, fieldName) => {
    return String(a[fieldName] || "").localeCompare(String(b[fieldName] || ""));
  };

  const getVoiceLabel = (record) => {
    return String(record.displayName || record.shortName || "");
  };

  const filteredPersonas = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    if (!search) {
      return personas;
    }

    return personas.filter((persona) => {
      return [persona.name, persona.displayName, persona.shortName]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search));
    });
  }, [personas, searchText]);

  const fetchPersonas = async () => {
    setLoading(true);

    try {
      const response = await personaApi.getPersonas();
      setPersonas(response.data);
    } catch (error) {
      console.error("Error fetching personas:", error);
      message.error("Failed to load personas.");
    } finally {
      setLoading(false);
    }
  };

  const fetchProfiles = async () => {
    try {
      const response = await personaApi.getProfiles();
      setProfiles(response.data);
    } catch (error) {
      console.error("Error fetching profiles:", error);
    }
  };

  const handleDelete = (record) => {
    confirm({
      title: "Are you sure?",
      content: "Do you want to delete this profile?",
      icon: <CloseCircleOutlined style={{ color: "red" }} />,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        try {
          await personaApi.deletePersona(record._id);
          message.success("Profile deleted successfully!");
          fetchPersonas();
        } catch (e) {
          console.error("Error deleting profile:", e);
          message.error("Failed to delete profile.");
        }
      },
    });
  };

  const handleEdit = async (record) => {
    try {
      const response = await personaApi.getPersona(record._id);
      setSelectedPersona(response.data);
      setIsEdit(true);
      setModalOpen(true);
    } catch (error) {
      console.error("Error fetching persona details:", error);
      message.error("Failed to load persona details.");
    }
  };

  const onAdd = () => {
    setSelectedPersona(null);
    setIsEdit(false);
    setModalOpen(true);
  };

  const handleCancel = () => {
    setModalOpen(false);
    setSelectedPersona(null);
    setIsEdit(false);
    form.resetFields();
  };

  useEffect(() => {
    fetchPersonas();
    fetchProfiles();
  }, []);

  const columns = [
    {
      title: "Avatar",
      dataIndex: "profile",
      key: "profile",
      width: 82,
      render: (url) => (
        <img
          src={url}
          alt="Profile"
          className="persona-avatar"
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => compareText(a, b, "name"),
      defaultSortOrder: "ascend",
      width: 150,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      responsive: ["md"],
      render: (value) => (
        <Paragraph ellipsis={{ rows: 2 }} className="table-copy">
          {value}
        </Paragraph>
      ),
    },
    {
      title: "Custom Instructions",
      dataIndex: "customInstructions",
      key: "customInstructions",
      responsive: ["lg"],
      render: (value) => (
        <Paragraph ellipsis={{ rows: 2 }} className="table-copy">
          {value}
        </Paragraph>
      ),
    },
    {
      title: "Voice",
      key: "voice",
      responsive: ["md"],
      sorter: (a, b) => getVoiceLabel(a).localeCompare(getVoiceLabel(b)),
      render: (_, record) => (
        <span style={{ fontWeight: 650 }}>{getVoiceLabel(record)}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 118,
      render: (_, record) => (
        <Space>
          <Button
            aria-label={`Edit ${record.name}`}
            onClick={() => handleEdit(record)}
          >
            <EditFilled />
          </Button>
          <Button
            danger
            aria-label={`Delete ${record.name}`}
            onClick={() => handleDelete(record)}
          >
            <DeleteFilled />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-shell">
      <div className="page-heading-row">
        <Title level={1}>Personas</Title>

        <Button type="primary" onClick={onAdd}>
          <PlusOutlined /> Create profile
        </Button>
      </div>

      <div className="page-toolbar">
        <Search
          allowClear
          enterButton="Search"
          placeholder="Search by name or voice"
          className="page-search"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          onSearch={(value) => setSearchText(value)}
        />

        <Button onClick={fetchPersonas} disabled={loading}>
          Refresh
        </Button>
      </div>

      <Table
        dataSource={filteredPersonas}
        columns={columns}
        loading={loading}
        pagination={false}
        rowKey="_id"
        scroll={{ x: 520 }}
      />

      <Modal
        title={isEdit ? "Edit Persona" : "Create Persona"}
        open={modalOpen}
        onCancel={handleCancel}
        footer={null}
        maskClosable={false}
        width="min(720px, calc(100vw - 24px))"
        className="persona-modal"
      >
        <AddPersona
          isEdit={isEdit}
          setModalOpen={setModalOpen}
          fetchPersonas={fetchPersonas}
          profiles={profiles}
          persona={selectedPersona}
          form={form}
          modalOpen={modalOpen}
        />
      </Modal>
    </div>
  );
};

export default PersonaTable;
